import apiClient from '../utils/axiosConfig.ts';
import { SupplierOrder, CustomerOrder, Supplier, CreateSupplierData, UpdateSupplierData, SupplierOriginType, TransportType, UserColumnPreferencesRequest, UserColumnPreferencesResponse, OrderEventLog, GenerateCustomsDeclarationsPayload, GenerateLoadDocumentPayload, CustomerQuote, SupplierQuote, QuoteStatus, RectificationReason, RectificationPreview, RectifyDetailPayload, RectifyDocumentItemPayload } from '../types.ts';

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await apiClient.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  changePassword: async (data: any) => {
    const response = await apiClient.post('/auth/change-password', data);
    return response.data;
  },

  refreshToken: async () => {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  },

  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },
};

// Permissions API (permessi per livello utente)
export const permissionsAPI = {
  getMyPermissions: async () => {
    const response = await apiClient.get('/permissions/me');
    return response.data;
  },

  getMatrix: async () => {
    const response = await apiClient.get('/permissions/matrix');
    return response.data;
  },

  updateMatrix: async (entries: { level_id: number; resource: string; permission: string }[]) => {
    const response = await apiClient.put('/permissions/matrix', { entries });
    return response.data;
  },
};

// App info API
export const appAPI = {
  getAppInfo: async () => {
    const response = await apiClient.get('/app/info');
    return response.data;
  },
};

// User API
export const userAPI = {
  getUsers: async (params?: any) => {
    const response = await apiClient.get('/users', { params });
    return response.data;
  },
  
  getUser: async (id: number) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  createUser: async (data: any) => {
    const response = await apiClient.post('/users', data);
    return response.data;
  },

  updateUser: async (id: number, data: any) => {
    const response = await apiClient.put(`/users/${id}`, data);
    return response.data;
  },

  deleteUser: async (id: number) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  },

  changeUserPassword: async (id: number, newPassword: string) => {
    const response = await apiClient.put(`/users/${id}/change-password`, { new_password: newPassword });
    return response.data;
  },
};

// User Level API
export const userLevelAPI = {
  getLevels: async () => {
    const response = await apiClient.get('/user-levels');
    return response.data;
  },
};

// Customer API
export const customerAPI = {
  getCustomers: async (search?: string) => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/customers', { params });
    return response.data;
  },
  
  getCustomer: async (id: number) => {
    const response = await apiClient.get(`/customers/${id}`);
    return response.data;
  },

  createCustomer: async (data: any) => {
    const response = await apiClient.post('/customers', data);
    return response.data;
  },

  updateCustomer: async (id: number, data: any) => {
    const response = await apiClient.put(`/customers/${id}`, data);
    return response.data;
  },

  deleteCustomer: async (id: number) => {
    const response = await apiClient.delete(`/customers/${id}`);
    return response.data;
  },
};

// Supplier API
// Supplier Origin Type API
export const supplierOriginTypeAPI = {
  getSupplierOriginTypes: async (): Promise<SupplierOriginType[]> => {
    const response = await apiClient.get('/supplier-origin-types');
    return response.data;
  },

  getSupplierOriginType: async (id: number): Promise<SupplierOriginType> => {
    const response = await apiClient.get(`/supplier-origin-types/${id}`);
    return response.data;
  },

  createSupplierOriginType: async (data: {code: string, description: string}): Promise<SupplierOriginType> => {
    const response = await apiClient.post('/supplier-origin-types', data);
    return response.data;
  },

  updateSupplierOriginType: async (id: number, data: {code?: string, description?: string}): Promise<SupplierOriginType> => {
    const response = await apiClient.put(`/supplier-origin-types/${id}`, data);
    return response.data;
  },

  deleteSupplierOriginType: async (id: number): Promise<void> => {
    const response = await apiClient.delete(`/supplier-origin-types/${id}`);
    return response.data;
  },
};

export const supplierAPI = {
  getSuppliers: async (search?: string): Promise<Supplier[]> => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/suppliers', { params });
    return response.data;
  },
  
  getSupplier: async (id: number): Promise<Supplier> => {
    const response = await apiClient.get(`/suppliers/${id}`);
    return response.data;
  },

  createSupplier: async (data: CreateSupplierData): Promise<Supplier> => {
    const response = await apiClient.post('/suppliers', data);
    return response.data;
  },

  updateSupplier: async (id: number, data: UpdateSupplierData): Promise<Supplier> => {
    const response = await apiClient.put(`/suppliers/${id}`, data);
    return response.data;
  },

  deleteSupplier: async (id: number): Promise<void> => {
    const response = await apiClient.delete(`/suppliers/${id}`);
    return response.data;
  },

   // Ordini fornitori (i preventivi fornitore sono in supplierQuoteAPI)
  getSupplierOrders: async (filters?: any): Promise<SupplierOrder[]> => {
    const params = new URLSearchParams();
    if (filters?.supplier_id) params.append('supplier_id', filters.supplier_id.toString());
    if (filters?.order_number) params.append('order_number', filters.order_number);
    if (filters?.order_status_id) params.append('order_status_id', filters.order_status_id.toString());
    if (filters?.product_id) params.append('product_id', filters.product_id.toString());
    if (filters?.exclude_completed) params.append('exclude_completed', filters.exclude_completed.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.limit !== undefined) params.append('limit', String(filters.limit));
    
    const response = await apiClient.get(`/supplier-orders?${params.toString()}`);
    return response.data;
  },

  getSupplierOrder: async (id: number): Promise<SupplierOrder> => {
    const response = await apiClient.get(`/supplier-orders/${id}`);
    return response.data;
  },

  createSupplierOrder: async (orderData: any): Promise<SupplierOrder> => {
    const response = await apiClient.post('/supplier-orders', orderData);
    return response.data;
  },

  updateSupplierOrder: async (id: number, orderData: any): Promise<SupplierOrder> => {
    const response = await apiClient.put(`/supplier-orders/${id}`, orderData);
    return response.data;
  },

  deleteSupplierOrder: async (id: number): Promise<void> => {
    await apiClient.delete(`/supplier-orders/${id}`);
  },

  updateSupplierOrderStatus: async (id: number, statusData: any): Promise<any> => {
    const response = await apiClient.put(`/supplier-orders/${id}/status`, statusData);
    return response.data;
  },

  /**
   * Ricevi merce da un ordine fornitore
   * Sposta quantità da in_production a available nel nuovo sistema product_stocks
   * 
   * @param id - ID dell'ordine fornitore
   * @param receiveData - Dati di ricezione { received_date: string, notes?: string }
   */
  receiveSupplierOrder: async (id: number, receiveData: {
    received_date: string;
    notes?: string;
  }): Promise<any> => {
    const response = await apiClient.post(`/supplier-orders/${id}/receive`, receiveData);
    return response.data;
  },

  /**
   * Annulla il residuo mai spedito di un ordine fornitore spedito parzialmente.
   * Incrementa quantity_cancelled sui dettagli e scarica la produzione residua.
   */
  cancelSupplierOrderRemainder: async (id: number, notes?: string): Promise<any> => {
    const response = await apiClient.post(`/supplier-orders/${id}/cancel-remainder`, notes ? { notes } : {});
    return response.data;
  },

  getSupplierOrderHistory: async (id: number): Promise<OrderEventLog[]> => {
    const response = await apiClient.get(`/supplier-orders/${id}/history`);
    return response.data;
  },

  // ---- Rettifica quantità della partita (motore lot_rectification) ----
  getRectificationReasons: async (): Promise<RectificationReason[]> => {
    const response = await apiClient.get('/supplier-orders/rectification/reasons');
    return response.data;
  },

  /** Anteprima (sola lettura): dove sta la merce della riga, fase proposta, effetti e blocchi. */
  previewDetailRectification: async (orderId: number, detailId: number, params: {
    new_quantity: number;
    stage?: string;
    shipment_id?: number | null;
    customs_document_item_id?: number | null;
    customs_rectified?: boolean | null;
  }): Promise<RectificationPreview> => {
    const query: Record<string, any> = { new_quantity: params.new_quantity };
    if (params.stage) query.stage = params.stage;
    if (params.shipment_id) query.shipment_id = params.shipment_id;
    if (params.customs_document_item_id) query.customs_document_item_id = params.customs_document_item_id;
    if (params.customs_rectified !== undefined && params.customs_rectified !== null) query.customs_rectified = params.customs_rectified;
    const response = await apiClient.get(`/supplier-orders/${orderId}/details/${detailId}/rectify-preview`, { params: query });
    return response.data;
  },

  /** Rettifica quantità della riga d'ordine (solo amministratore): cascata su produzione/spedizione/bolletta. */
  rectifyDetailQuantity: async (orderId: number, detailId: number, payload: RectifyDetailPayload): Promise<any> => {
    const response = await apiClient.post(`/supplier-orders/${orderId}/details/${detailId}/rectify`, payload);
    return response.data;
  },
};


// Importer API
export const importerAPI = {
  getImporters: async (search?: string) => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/importers', { params });
    return response.data;
  },
  
  getImporter: async (id: number) => {
    const response = await apiClient.get(`/importers/${id}`);
    return response.data;
  },

  createImporter: async (data: any) => {
    const response = await apiClient.post('/importers', data);
    return response.data;
  },

  updateImporter: async (id: number, data: any) => {
    const response = await apiClient.put(`/importers/${id}`, data);
    return response.data;
  },

  deleteImporter: async (id: number) => {
    const response = await apiClient.delete(`/importers/${id}`);
    return response.data;
  },
};

// Product API
export const productAPI = {
  getProducts: async (search?: string, customer_id?: number, supplier_id?: number) => {
    const params: any = {};
    if (search) params.search = search;
    if (customer_id) params.customer_id = customer_id;
    if (supplier_id) params.supplier_id = supplier_id;
    const response = await apiClient.get('/products', { params });
    return response.data;
  },
  
  getProduct: async (id: number) => {
    const response = await apiClient.get(`/products/${id}`);
    return response.data;
  },

  createProduct: async (data: any) => {
    const response = await apiClient.post('/products', data);
    return response.data;
  },

  updateProduct: async (id: number, data: any) => {
    const response = await apiClient.put(`/products/${id}`, data);
    return response.data;
  },

  deleteProduct: async (id: number) => {
    const response = await apiClient.delete(`/products/${id}`);
    return response.data;
  },
};

// Warehouse API
export const warehouseAPI = {
  getWarehouses: async (search?: string) => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/warehouses', { params });
    return response.data;
  },
  
  getWarehouse: async (id: number) => {
    const response = await apiClient.get(`/warehouses/${id}`);
    return response.data;
  },

  createWarehouse: async (data: any) => {
    const response = await apiClient.post('/warehouses', data);
    return response.data;
  },

  updateWarehouse: async (id: number, data: any) => {
    const response = await apiClient.put(`/warehouses/${id}`, data);
    return response.data;
  },

  deleteWarehouse: async (id: number) => {
    const response = await apiClient.delete(`/warehouses/${id}`);
    return response.data;
  }
};

// Courier API
export const courierAPI = {
  getCouriers: async (search?: string) => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/couriers', { params });
    return response.data;
  },
  
  getCourier: async (id: number) => {
    const response = await apiClient.get(`/couriers/${id}`);
    return response.data;
  },

  createCourier: async (data: any) => {
    const response = await apiClient.post('/couriers', data);
    return response.data;
  },

  updateCourier: async (id: number, data: any) => {
    const response = await apiClient.put(`/couriers/${id}`, data);
    return response.data;
  },

  deleteCourier: async (id: number) => {
    const response = await apiClient.delete(`/couriers/${id}`);
    return response.data;
  }
};

// Dashboard API
export const dashboardAPI = {
  getAlerts: async (customerId?: number) => {
    const params: any = {};
    if (customerId) params.customer_id = customerId;
    const response = await apiClient.get('/dashboard/alerts', { params });
    return response.data;
  },

  getKpi: async (customerId?: number, days: number = 30) => {
    const params: any = { days };
    if (customerId) params.customer_id = customerId;
    const response = await apiClient.get('/dashboard/kpi', { params });
    return response.data;
  },

  getCharts: async (customerId?: number, months: number = 6) => {
    const params: any = { months };
    if (customerId) params.customer_id = customerId;
    const response = await apiClient.get('/dashboard/charts', { params });
    return response.data;
  }
};



// Stock API
export const stockAPI = {
  getStocks: async (filters?: any) => {
    const response = await apiClient.get('/stock', { params: filters });
    return response.data;
  },

  getStock: async (id: number) => {
    const response = await apiClient.get(`/stock/${id}`);
    return response.data;
  },

  createStock: async (data: any) => {
    const response = await apiClient.post('/stock', data);
    return response.data;
  },

  updateStock: async (id: number, data: any) => {
    const response = await apiClient.put(`/stock/${id}`, data);
    return response.data;
  },

  deleteStock: async (id: number) => {
    const response = await apiClient.delete(`/stock/${id}`);
    return response.data;
  },

  getStockSummary: async (filters?: any) => {
    const response = await apiClient.get('/inventory/stock-summary', { params: filters });
    return response.data;
  },

  getDetailedStockSummary: async (filters?: any) => {
    // Filtra i parametri undefined prima di inviarli
    const cleanParams = filters ? Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as any) : {};
    
    const response = await apiClient.get('/stock/detailed-summary', { params: cleanParams });
    return response.data;
  },

  getWarehouseSummary: async (warehouseId?: number) => {
    const params = warehouseId ? { warehouse_id: warehouseId } : {};
    const response = await apiClient.get('/stock/analytics/warehouse-summary', { params });
    return response.data;
  },

  getCustomerSummary: async (customerId?: number) => {
    const params = customerId ? { customer_id: customerId } : {};
    const response = await apiClient.get('/stock/analytics/customer-summary', { params });
    return response.data;
  },
  getStockMovements: async (filters?: any) => {
    const response = await apiClient.get('/stock-movements', { params: filters });
    return response.data;
  },

  getStockMovement: async (id: number) => {
    const response = await apiClient.get(`/stock-movements/${id}`);
    return response.data;
  },

  createStockMovement: async (stockId: number, data: any) => {
    const response = await apiClient.post(`/stock/${stockId}/movements`, data);
    return response.data;
  },

  createStockMovementDirect: async (data: any) => {
    const response = await apiClient.post('/stock-movements', data);
    return response.data;
  },

  updateStockMovement: async (id: number, data: any) => {
    const response = await apiClient.put(`/stock-movements/${id}`, data);
    return response.data;
  },

  deleteStockMovement: async (id: number) => {
    const response = await apiClient.delete(`/stock-movements/${id}`);
    return response.data;
  },

  getStockMovementsSummary: async (filters?: any) => {
    const response = await apiClient.get('/stock-movements/analytics/summary', { params: filters });
    return response.data;
  },

  // getUDCs rimosso - tabelle UDC non esistono più

  changeProductStatus: async (stockId: number, data: any) => {
    const response = await apiClient.post(`/stock/${stockId}/change-product-status`, data);
    return response.data;
  },
};

// Stati preventivo (tabella unica per preventivi cliente e fornitore)
export const quoteStatusAPI = {
  getQuoteStatuses: async (activeOnly: boolean = true): Promise<QuoteStatus[]> => {
    const response = await apiClient.get('/quote-statuses/', { params: { active_only: activeOnly } });
    return response.data;
  },
};

// Filtri delle liste preventivi (limit 0 = elenco completo, come gli ordini)
export interface QuoteListFilters {
  skip?: number;
  limit?: number;
  customer_id?: number;
  supplier_id?: number;
  customer_order_id?: number;
  status_id?: number;
  quote_number?: string;
  customer_reference?: string;
  product_id?: number;
  date_from?: string;
  date_to?: string;
  exclude_closed?: boolean;
}

const buildQuoteParams = (filters?: QuoteListFilters) => {
  const params: any = {};
  if (!filters) return params;
  if (filters.skip !== undefined) params.skip = filters.skip;
  if (filters.limit !== undefined) params.limit = filters.limit;
  if (filters.customer_id) params.customer_id = filters.customer_id;
  if (filters.supplier_id) params.supplier_id = filters.supplier_id;
  if (filters.customer_order_id) params.customer_order_id = filters.customer_order_id;
  if (filters.status_id) params.status_id = filters.status_id;
  if (filters.quote_number) params.quote_number = filters.quote_number;
  if (filters.customer_reference) params.customer_reference = filters.customer_reference;
  if (filters.product_id) params.product_id = filters.product_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  if (filters.exclude_closed !== undefined) params.exclude_closed = filters.exclude_closed;
  return params;
};

// Customer Quote API (slash finale sulle liste: evita il redirect 307)
export const customerQuoteAPI = {
  getCustomerQuotes: async (filters?: QuoteListFilters): Promise<CustomerQuote[]> => {
    const response = await apiClient.get('/customer-quotes/', { params: buildQuoteParams(filters) });
    return response.data;
  },

  getCustomerQuote: async (id: number): Promise<CustomerQuote> => {
    const response = await apiClient.get(`/customer-quotes/${id}`);
    return response.data;
  },

  getCustomerQuoteHistory: async (id: number): Promise<OrderEventLog[]> => {
    const response = await apiClient.get(`/customer-quotes/${id}/history`);
    return response.data;
  },

  createCustomerQuote: async (data: any): Promise<CustomerQuote> => {
    const response = await apiClient.post('/customer-quotes/', data);
    return response.data;
  },

  updateCustomerQuote: async (id: number, data: any): Promise<CustomerQuote> => {
    const response = await apiClient.put(`/customer-quotes/${id}`, data);
    return response.data;
  },

  deleteCustomerQuote: async (id: number) => {
    const response = await apiClient.delete(`/customer-quotes/${id}`);
    return response.data;
  },

  convertToOrder: async (id: number, data: any) => {
    const response = await apiClient.post(`/customer-quotes/${id}/convert-to-order`, data);
    return response.data;
  },

  checkAvailability: async (id: number) => {
    const response = await apiClient.post(`/customer-quotes/${id}/check-availability`);
    return response.data;
  },

  updateQuoteStatus: async (id: number, statusData: { status_id: number; notes?: string }) => {
    const response = await apiClient.put(`/customer-quotes/${id}/status`, statusData);
    return response.data;
  },

  duplicateQuote: async (id: number, data: { quote_number: string; quote_date?: string; expiry_date?: string }): Promise<CustomerQuote> => {
    const response = await apiClient.post(`/customer-quotes/${id}/duplicate`, data);
    return response.data;
  },

  checkProductAvailability: async (productId: number) => {
    const response = await apiClient.get(`/customer-quotes/product-availability/${productId}`);
    return response.data;
  },

  getProductStocks: async (productId: number) => {
    const response = await apiClient.get(`/customer-quotes/product-stocks/${productId}`);
    return response.data;
  },
};

// Supplier Quote API (preventivi fornitore, stesso standard degli ordini fornitore)
export const supplierQuoteAPI = {
  getSupplierQuotes: async (filters?: QuoteListFilters): Promise<SupplierQuote[]> => {
    const response = await apiClient.get('/supplier-quotes/', { params: buildQuoteParams(filters) });
    return response.data;
  },

  getSupplierQuote: async (id: number): Promise<SupplierQuote> => {
    const response = await apiClient.get(`/supplier-quotes/${id}`);
    return response.data;
  },

  getSupplierQuoteHistory: async (id: number): Promise<OrderEventLog[]> => {
    const response = await apiClient.get(`/supplier-quotes/${id}/history`);
    return response.data;
  },

  createSupplierQuote: async (data: any): Promise<SupplierQuote> => {
    const response = await apiClient.post('/supplier-quotes/', data);
    return response.data;
  },

  updateSupplierQuote: async (id: number, data: any): Promise<SupplierQuote> => {
    const response = await apiClient.put(`/supplier-quotes/${id}`, data);
    return response.data;
  },

  deleteSupplierQuote: async (id: number) => {
    const response = await apiClient.delete(`/supplier-quotes/${id}`);
    return response.data;
  },

  updateQuoteStatus: async (id: number, statusData: { status_id: number; notes?: string }) => {
    const response = await apiClient.put(`/supplier-quotes/${id}/status`, statusData);
    return response.data;
  },

  convertToOrder: async (id: number, data: { order_number: string; order_date?: string; delivery_date?: string | null; notes?: string | null }) => {
    const response = await apiClient.post(`/supplier-quotes/${id}/convert-to-order`, data);
    return response.data;
  },
};

// Customer Order API
export const customerOrderAPI = {
  getCustomerOrders: async (filters?: {
    skip?: number;
    limit?: number;
    customer_id?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
    order_status_id?: number;
    product_id?: number;
    order_number?: string;
    customer_order_number?: string;
    exclude_completed?: boolean;
  }): Promise<CustomerOrder[]> => {
    const params: any = {};
    if (filters) {
      if (filters.skip !== undefined) params.skip = filters.skip;
      if (filters.limit !== undefined) params.limit = filters.limit;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.order_status_id) params.order_status_id = filters.order_status_id;
      if (filters.product_id) params.product_id = filters.product_id;
      if (filters.order_number) params.order_number = filters.order_number;
      if (filters.customer_order_number) params.customer_order_number = filters.customer_order_number;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.exclude_completed !== undefined) params.exclude_completed = filters.exclude_completed;
    }
    
    const response = await apiClient.get('/customer-orders/', { params });
    return response.data;
  },

  getCustomerOrder: async (id: number): Promise<CustomerOrder> => {
    const response = await apiClient.get(`/customer-orders/${id}`);
    return response.data;
  },

  getCustomerOrderHistory: async (id: number): Promise<OrderEventLog[]> => {
    const response = await apiClient.get(`/customer-orders/${id}/history`);
    return response.data;
  },

  createCustomerOrder: async (data: any): Promise<CustomerOrder> => {
    const response = await apiClient.post('/customer-orders/', data);
    return response.data;
  },

  updateCustomerOrder: async (id: number, data: any): Promise<CustomerOrder> => {
    const response = await apiClient.put(`/customer-orders/${id}`, data);
    return response.data;
  },

  deleteCustomerOrder: async (id: number): Promise<void> => {
    await apiClient.delete(`/customer-orders/${id}`);
  },
  updateOrderStatus: async (id: number, data: any) => {
    const response = await apiClient.put(`/customer-orders/${id}/status`, data);
    return response.data;
  },

  reserveStock: async (orderId: number, reservationData: {
    product_id: number;
    stock_code: string;
    quantity_desired: number;
    force_cross_customer?: boolean;
  }) => {
    const response = await apiClient.post(`/customer-orders/${orderId}/reserve-stock`, reservationData);
    return response.data;
  },

  getOrderCart: async (orderId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/cart`);
    return response.data;
  },

  removeCartItem: async (orderId: number, cartItemId: number) => {
    const response = await apiClient.delete(`/customer-orders/${orderId}/cart/${cartItemId}`);
    return response.data;
  },

  checkOrderCompleteness: async (orderId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/completeness`);
    return response.data;
  },

  getOrderInventory: async (orderId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/inventory`);
    return response.data;
  },

  getProductReservations: async (orderId: number, productId: number, stockId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/product/${productId}/stock/${stockId}/reservations`);
    return response.data;
  },

  updateProductReservations: async (orderId: number, productId: number, stockId: number, reservationData: {
    available: number;
    in_transit: number;
    in_production: number;
    unit_price?: number;
    force_cross_customer?: boolean;
  }) => {
    const response = await apiClient.put(`/customer-orders/${orderId}/product/${productId}/stock/${stockId}/update-reservations`, reservationData);
    return response.data;
  },

  // Opziona tutti i disponibili: anteprima (proposta FIFO e giacenze per la scelta)
  reserveAvailablePreview: async (orderId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/reserve-available/preview`);
    return response.data;
  },

  // Opziona tutti i disponibili: esecuzione; allocations = {product_id: {stock_id: quantità}}
  // solo per i prodotti in cui l'utente ha scelto le giacenze
  reserveAvailable: async (orderId: number, allocations?: Record<number, Record<number, number>>) => {
    const response = await apiClient.post(`/customer-orders/${orderId}/reserve-available`, { allocations: allocations || null });
    return response.data;
  },

  downloadPdfRichiestaColli: async (orderId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/pdf-richiesta-colli`, {
      responseType: 'blob'
    });
    return response;
  },

  downloadXlsRichiestaColli: async (orderId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/xls-richiesta-colli`, {
      responseType: 'blob'
    });
    return response;
  },

  // --- Copertura ordini stock (registro customer_order_stock_loads) ---
  getStockLoads: async (orderId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/stock-loads`);
    return response.data;
  },

  getStockLoadCandidates: async (orderId: number, productId: number) => {
    const response = await apiClient.get(`/customer-orders/${orderId}/stock-loads/candidates/${productId}`);
    return response.data;
  },

  createStockLoad: async (orderId: number, data: { stock_id: number; quantity: number; notes?: string }) => {
    const response = await apiClient.post(`/customer-orders/${orderId}/stock-loads`, data);
    return response.data;
  },

  deleteStockLoad: async (orderId: number, loadId: number) => {
    const response = await apiClient.delete(`/customer-orders/${orderId}/stock-loads/${loadId}`);
    return response.data;
  },

};

// Product Stock Lock API
export const productStockLockAPI = {
  acquireLock: async (stockId: number) => {
    const response = await apiClient.post(`/product-stocks/${stockId}/lock`);
    return response.data;
  },
  releaseLock: async (stockId: number) => {
    const response = await apiClient.delete(`/product-stocks/${stockId}/lock`);
    return response.data;
  },
};

// Order Status API
export const orderStatusAPI = {
  getOrderStatuses: async () => {
    try {
      const response = await apiClient.get('/order-statuses');
      return response.data;
    } catch (error) {
      console.error('Error fetching order statuses:', error);
      throw error;
    }
  }
};

// Shipping Destination API
export const shippingDestinationAPI = {
  getShippingDestinations: async (search?: string, customerId?: number) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (customerId) params.append('customer_id', customerId.toString());
    
    const response = await apiClient.get(`/shipping-destinations?${params}`);
    return response.data;
  },

  getShippingDestination: async (id: number) => {
    const response = await apiClient.get(`/shipping-destinations/${id}`);
    return response.data;
  },

  createShippingDestination: async (data: any) => {
    const response = await apiClient.post('/shipping-destinations', data);
    return response.data;
  },

  updateShippingDestination: async (id: number, data: any) => {
    const response = await apiClient.put(`/shipping-destinations/${id}`, data);
    return response.data;
  },

  deleteShippingDestination: async (id: number) => {
    const response = await apiClient.delete(`/shipping-destinations/${id}`);
    return response.data;
  }
};

// Inventory API
export const inventoryAPI = {
  getInventoryStocks: async (filters?: any, pagination?: {skip: number, limit: number}, sorting?: {sort_by: string, sort_direction: string}) => {
    const params = {...filters, ...pagination, ...sorting};
    const response = await apiClient.get('/inventory', { params });
    return response.data;
  },
  
  getInventoryStock: async (id: number) => {
    const response = await apiClient.get(`/inventory/${id}`);
    return response.data;
  },
  
  createInventoryStock: async (data: any) => {
    const response = await apiClient.post('/inventory', data);
    return response.data;
  },
  
  updateInventoryStock: async (id: number, data: any) => {
    const response = await apiClient.put(`/inventory/${id}`, data);
    return response.data;
  },
  
  deleteInventoryStock: async (id: number) => {
    const response = await apiClient.delete(`/inventory/${id}`);
    return response.data;
  },
  
  getFilterOptions: async () => {
    const response = await apiClient.get('/inventory/filter-options');
    return response.data;
  },

  // Ricerca lato server degli ordini cliente (l'elenco di filter-options è
  // troncato ai 100 più recenti e non permette di collegare ordini vecchi)
  searchCustomerOrders: async (q?: string, includeId?: number) => {
    const response = await apiClient.get('/inventory/customer-orders/search', {
      params: { q: q || undefined, include_id: includeId || undefined }
    });
    return response.data;
  },

  getStockReservations: async (stockId: number) => {
    const response = await apiClient.get(`/inventory/${stockId}/reservations`);
    return response.data;
  },

  getStockHistory: async (stockId: number) => {
    const response = await apiClient.get(`/inventory/${stockId}/history`);
    return response.data;
  },

  // Split stock into multiple UDC
  splitStock: async (stockId: number, splitData: {
    splits: Array<{
      udc_pallet?: string | null;
      udc_collo?: string | null;
      quantity: number;
    }>
  }) => {
    const response = await apiClient.post(`/inventory/${stockId}/split`, splitData);
    return response.data;
  },

  // Transfer stock to another warehouse
  transferStockWarehouse: async (stockId: number, transferData: {
    destination_warehouse_id: number;
    quantity: number;
    reason: string;
    notes?: string;
  }) => {
    const response = await apiClient.post(`/inventory/${stockId}/transfer-warehouse`, transferData);
    return response.data;
  },

  // Change UDC assignment
  changeStockUdc: async (stockId: number, udcData: {
    udc_pallet?: string;
    udc_collo?: string;
    reason: string;
    notes?: string;
  }) => {
    const response = await apiClient.post(`/inventory/${stockId}/change-udc`, udcData);
    return response.data;
  },

  // Transfer stock between UDCs
  transferStockToUdc: async (stockId: number, transferData: {
    quantity: number;
    destination_udc_pallet: string;
    destination_udc_collo: string;
    reason: string;
    notes?: string;
  }) => {
    const response = await apiClient.post(`/inventory/${stockId}/transfer-udc`, transferData);
    return response.data;
  },

  // Adjust stock quantity
  adjustStock: async (stockId: number, adjustmentData: {
    adjustment_type: 'ADD' | 'REMOVE' | 'SET';
    quantity: number;
    source_field: 'available' | 'in_production' | 'in_transit';
    reason: string;
    notes?: string;
  }) => {
    const response = await apiClient.post(`/inventory/${stockId}/adjust`, adjustmentData);
    return response.data;
  },

  // Export Excel
  exportInventoryExcel: async (filters?: any, sorting?: {sort_by: string, sort_direction: string}) => {
    const params = {...filters, ...sorting};
    const response = await apiClient.get('/inventory/export/excel', { 
      params,
      responseType: 'blob'
    });
    
    // Crea download automatico del file
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Estrai nome file dall'header se disponibile
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'inventario_export.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename=(.+)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/"/g, '');
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response.data;
  },

  // Import Excel
  importInventoryExcel: async (file: File, mode: string = 'update') => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/inventory/import/excel', formData, {
      params: { mode },
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  getProductAvailabilitySummary: async (productId: number) => {
    const response = await apiClient.get(`/inventory/product/${productId}/availability-summary`);
    return response.data;
  }
};

// Container Types API
export const containerTypeAPI = {
  getContainerTypes: async (filters?: {is_active?: boolean, search?: string, skip?: number, limit?: number}) => {
    const params = filters;
    const response = await apiClient.get('/container-types', { params });
    return response.data;
  },
  
  getContainerType: async (id: number) => {
    const response = await apiClient.get(`/container-types/${id}`);
    return response.data;
  },
  
  createContainerType: async (data: any) => {
    const response = await apiClient.post('/container-types', data);
    return response.data;
  },
  
  updateContainerType: async (id: number, data: any) => {
    const response = await apiClient.put(`/container-types/${id}`, data);
    return response.data;
  },
  
  deleteContainerType: async (id: number) => {
    const response = await apiClient.delete(`/container-types/${id}`);
    return response.data;
  },
  
  getActiveContainerTypes: async () => {
    const response = await apiClient.get('/container-types/active/list');
    return response.data;
  }
};

// International Shipment Statuses API
export const internationalShipmentStatusAPI = {
  getInternationalShipmentStatuses: async (filters?: {is_active?: boolean, search?: string, skip?: number, limit?: number}) => {
    const params = filters;
    const response = await apiClient.get('/international-shipment-statuses', { params });
    return response.data;
  },
  
  getInternationalShipmentStatus: async (id: number) => {
    const response = await apiClient.get(`/international-shipment-statuses/${id}`);
    return response.data;
  },
  
  createInternationalShipmentStatus: async (data: any) => {
    const response = await apiClient.post('/international-shipment-statuses', data);
    return response.data;
  },
  
  updateInternationalShipmentStatus: async (id: number, data: any) => {
    const response = await apiClient.put(`/international-shipment-statuses/${id}`, data);
    return response.data;
  },
  
  deleteInternationalShipmentStatus: async (id: number) => {
    const response = await apiClient.delete(`/international-shipment-statuses/${id}`);
    return response.data;
  },
  
  getActiveInternationalShipmentStatuses: async () => {
    const response = await apiClient.get('/international-shipment-statuses/active/list');
    return response.data;
  }
};

// IM7 Documents API
export const customsAPI = {
  getCustomsDocuments: async (filters?: {
    skip?: number;
    limit?: number;
    document_type?: string;
    status?: string;
    warehouse_id?: number;
    is_manual?: boolean;
    shipment_id?: number;
    search?: string;
    date_from?: string;
    date_to?: string;
  }) => {
    const params = filters;
    const response = await apiClient.get('/customs-documents', { params });

    // Return complete response structure for pagination
    return response.data;
  },

  getCustomsDocument: async (id: number) => {
    const response = await apiClient.get(`/customs-documents/${id}`);
    return response.data;
  },

  createCustomsDocument: async (data: any) => {
    const response = await apiClient.post('/customs-documents', data);
    return response.data;
  },

  updateCustomsDocument: async (id: number, data: any) => {
    const response = await apiClient.put(`/customs-documents/${id}`, data);
    return response.data;
  },

  deleteCustomsDocument: async (id: number) => {
    const response = await apiClient.delete(`/customs-documents/${id}`);
    return response.data;
  },

  processCustomsDocument: async (id: number) => {
    const response = await apiClient.post(`/customs-documents/${id}/process`);
    return response.data;
  },

  getArrivedContainers: async () => {
    const response = await apiClient.get('/customs-documents/containers/arrived');
    return response.data;
  },

  getContainerItems: async (id: number) => {
    const response = await apiClient.get(`/customs-documents/${id}/container-items`);
    return response.data;
  },

  getCustomsBolletta: async (id: number) => {
    const response = await apiClient.get(`/customs-documents/${id}/bolletta`);
    return response.data;
  },

  exportCustomsReport: async (id: number, format: 'pdf' | 'excel' = 'pdf') => {
    const response = await apiClient.get(`/customs-documents/${id}/export`, {
      params: { format },
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `report_doganale_${id}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  createManualDocument: async (data: any) => {
    // L'endpoint FastAPI ora usa un JSON body con schema Pydantic
    const requestData = {
      document_type: data.document_type || 'IM7',
      document_number: data.document_number,
      document_date: data.document_date,
      warehouse_id: data.warehouse_id,
      products: [], // Array JavaScript nativo (vuoto per ora)
      reason: "Caricamento storico manuale",
      ...(data.notes && { notes: data.notes })
    };

    const response = await apiClient.post('/customs-documents/manual', requestData);
    return response.data;
  },

  updateContainerItemSingolo: async (docId: number, itemId: number, singolo: number | null) => {
    const response = await apiClient.put(`/customs-documents/${docId}/container-items/${itemId}/singolo`, {
      singolo: singolo
    });
    return response.data;
  },

  // Customs Items endpoints
  getCustomsItems: async (id: number) => {
    const response = await apiClient.get(`/customs-documents/${id}/items`);
    return response.data;
  },

  getCustomsBalance: async (id: number) => {
    const response = await apiClient.get(`/customs-documents/${id}/balance`);
    return response.data;
  },

  updateCustomsItem: async (docId: number, itemId: number, data: any) => {
    const response = await apiClient.put(`/customs-documents/${docId}/items/${itemId}`, data);
    return response.data;
  },

  updateProcessedItemValues: async (docId: number, itemId: number, data: { unit_cost?: number, unit_price?: number, weight_gross?: number, stock_code?: string | null }) => {
    const response = await apiClient.put(`/customs-documents/${docId}/items/${itemId}/update-values`, data);
    return response.data;
  },

  /** Anteprima rettifica quantità dalla riga del documento (bozza → transito, elaborato → disponibile). */
  previewItemRectification: async (docId: number, itemId: number, params: {
    new_quantity: number;
    customs_rectified?: boolean | null;
    supplier_order_detail_id?: number | null;
  }): Promise<RectificationPreview> => {
    const query: Record<string, any> = { new_quantity: params.new_quantity };
    if (params.customs_rectified !== undefined && params.customs_rectified !== null) query.customs_rectified = params.customs_rectified;
    if (params.supplier_order_detail_id) query.supplier_order_detail_id = params.supplier_order_detail_id;
    const response = await apiClient.get(`/customs-documents/${docId}/items/${itemId}/rectify-preview`, { params: query });
    return response.data;
  },

  /** Rettifica quantità di una riga di documento ELABORATO (giacenza ± delta con movimento, bolletta e spedizione/ordine allineati). */
  rectifyItemQuantity: async (docId: number, itemId: number, payload: RectifyDocumentItemPayload): Promise<any> => {
    const response = await apiClient.post(`/customs-documents/${docId}/items/${itemId}/rectify-quantity`, payload);
    return response.data;
  },

  createCustomsItem: async (docId: number, data: any) => {
    const response = await apiClient.post(`/customs-documents/${docId}/items`, data);
    return response.data;
  },

  deleteCustomsItem: async (docId: number, itemId: number) => {
    const response = await apiClient.delete(`/customs-documents/${docId}/items/${itemId}`);
    return response.data;
  },

  // Manual customs document creation
  createManualCustomsDocument: async (data: {
    document_type: string;
    document_number: string;
    document_date: string;
    warehouse_id: number;
    products: Array<{
      product_id: number;
      quantity: number;
      unit_cost?: number;
      unit_price?: number;
      udc_code?: string;
    }>;
    reason?: string;
    notes?: string;
  }) => {
    const response = await apiClient.post('/customs-documents/manual', data);
    return response.data;
  }
};

// Transport Types API
export const transportTypeAPI = {
  getTransportTypes: async (): Promise<TransportType[]> => {
    const response = await apiClient.get('/transport-types');
    return response.data;
  },

  getTransportType: async (id: number): Promise<TransportType> => {
    const response = await apiClient.get(`/transport-types/${id}`);
    return response.data;
  },
};

// Cargo Loading API (unified system)
export const cargoLoadingAPI = {
  // Get cargo loading history with filters
  getCargoHistory: async (filters?: any): Promise<any> => {
    const response = await apiClient.get('/cargo-loading/history', { params: filters });
    return response.data;
  },

  // Create DDT document
  createDDT: async (data: any): Promise<any> => {
    const response = await apiClient.post('/cargo-loading/ddt', data);
    return response.data;
  },

  // Get cargo types and flows
  getCargoFlows: async (): Promise<any> => {
    const response = await apiClient.get('/cargo-loading/flows');
    return response.data;
  },

  // Automatic DDT creation from supplier order
  createAutomaticDDT: async (supplierOrderId: number, data: any): Promise<any> => {
    const response = await apiClient.post(`/cargo-loading/auto-ddt/${supplierOrderId}`, data);
    return response.data;
  },
};

// Legacy alias for backward compatibility with specific method mapping
export const im7API = {
  getIM7Documents: customsAPI.getCustomsDocuments,
  getIM7Document: customsAPI.getCustomsDocument,
  createIM7Document: customsAPI.createCustomsDocument,
  updateIM7Document: customsAPI.updateCustomsDocument,
  deleteIM7Document: customsAPI.deleteCustomsDocument,
  processIM7Document: customsAPI.processCustomsDocument,
  getArrivedContainers: customsAPI.getArrivedContainers,
  getContainerItems: customsAPI.getContainerItems,
  getIM7Bolletta: customsAPI.getCustomsBolletta,
  getCustomsBolletta: customsAPI.getCustomsBolletta,
  exportCustomsReport: customsAPI.exportCustomsReport,
  updateContainerItemSingolo: customsAPI.updateContainerItemSingolo,
  getCustomsItems: customsAPI.getCustomsItems,
  getCustomsBalance: customsAPI.getCustomsBalance,
  updateCustomsItem: customsAPI.updateCustomsItem,
  createCustomsItem: customsAPI.createCustomsItem,
  deleteCustomsItem: customsAPI.deleteCustomsItem,
  createManualCustomsDocument: customsAPI.createManualCustomsDocument
};

// Transport Types API
export const transportTypesAPI = {
  getTransportTypes: async (params?: any) => {
    const response = await apiClient.get('/transport-types', { params });
    return response.data;
  },

  getTransportType: async (id: number) => {
    const response = await apiClient.get(`/transport-types/${id}`);
    return response.data;
  },

  createTransportType: async (data: any) => {
    const response = await apiClient.post('/transport-types', data);
    return response.data;
  },

  updateTransportType: async (id: number, data: any) => {
    const response = await apiClient.put(`/transport-types/${id}`, data);
    return response.data;
  },

  deleteTransportType: async (id: number) => {
    const response = await apiClient.delete(`/transport-types/${id}`);
    return response.data;
  },

  getAllTransportTypes: async () => {
    const response = await apiClient.get('/transport-types/list/all');
    return response.data;
  },
};

// Unified Shipments API
export const unifiedShipmentsAPI = {
  // CRUD principale
  getUnifiedShipments: async (params?: any) => {
    const response = await apiClient.get('/unified-shipments', { params });
    return response.data;
  },

  getUnifiedShipment: async (id: number) => {
    const response = await apiClient.get(`/unified-shipments/${id}`);
    return response.data;
  },

  createUnifiedShipment: async (data: any) => {
    const response = await apiClient.post('/unified-shipments', data);
    return response.data;
  },

  updateUnifiedShipment: async (id: number, data: any) => {
    const response = await apiClient.put(`/unified-shipments/${id}`, data);
    return response.data;
  },

  deleteUnifiedShipment: async (id: number) => {
    const response = await apiClient.delete(`/unified-shipments/${id}`);
    return response.data;
  },

  // Dashboard e statistiche
  getDashboardStats: async () => {
    const response = await apiClient.get('/unified-shipments/dashboard/stats');
    return response.data;
  },

  // Ordini pronti per spedizione (filtri: fornitore e/o tipo spedizione per consolidate multi-fornitore)
  getReadyToShipOrders: async (filters?: { supplier_id?: number; shipment_type?: string }) => {
    const params: any = {};
    if (filters?.supplier_id) params.supplier_id = filters.supplier_id;
    if (filters?.shipment_type) params.shipment_type = filters.shipment_type;
    const response = await apiClient.get('/unified-shipments/ready-orders', { params });
    return response.data;
  },

  // Fornitori con ordini pronti
  getSuppliersWithReadyOrders: async () => {
    const response = await apiClient.get('/unified-shipments/suppliers/with-ready-orders');
    return response.data;
  },

  // Magazzini disponibili
  getWarehouses: async (shipmentType?: string) => {
    const params = shipmentType ? { shipment_type: shipmentType } : {};
    const response = await apiClient.get('/unified-shipments/warehouses/list', { params });
    return response.data;
  },

  // Tipi trasporto
  getTransportTypes: async () => {
    const response = await apiClient.get('/unified-shipments/transport-types');
    return response.data;
  },

  // Tipi container (solo per EXTRA_UE)
  getContainerTypes: async () => {
    const response = await apiClient.get('/unified-shipments/container-types');
    return response.data;
  },

  // Stati spedizione
  getStatuses: async () => {
    const response = await apiClient.get('/unified-shipments/statuses');
    return response.data;
  },

  // Items (Packing List)
  getShipmentItems: async (shipmentId: number) => {
    const response = await apiClient.get(`/unified-shipments/${shipmentId}/items`);
    return response.data;
  },

  addShipmentItem: async (shipmentId: number, data: any) => {
    const response = await apiClient.post(`/unified-shipments/${shipmentId}/items`, data);
    return response.data;
  },

  updateShipmentItem: async (shipmentId: number, itemId: number, data: any) => {
    const response = await apiClient.put(`/unified-shipments/${shipmentId}/items/${itemId}`, data);
    return response.data;
  },

  deleteShipmentItem: async (shipmentId: number, itemId: number) => {
    const response = await apiClient.delete(`/unified-shipments/${shipmentId}/items/${itemId}`);
    return response.data;
  },

  // Packing List per documenti doganali
  getPackingListForCustoms: async (shipmentId: number) => {
    const response = await apiClient.get(`/unified-shipments/${shipmentId}/packing-list`);
    return response.data;
  },

  // Generazione automatica IM7 da spedizione arrivata
  generateIM7: async (shipmentId: number, data: { document_number: string; document_date: string; mnr_code?: string }) => {
    const response = await apiClient.post(`/unified-shipments/${shipmentId}/generate-im7`, data);
    return response.data;
  },

  // Packing list della spedizione (righe con prodotto/quantità) per pre-popolare lo split
  getPackingList: async (shipmentId: number): Promise<any> => {
    const response = await apiClient.get(`/unified-shipments/${shipmentId}/packing-list`);
    return response.data;
  },

  // Generazione delle dichiarazioni H2 (IM7) / H1 (IM4) da spedizione arrivata (più dichiarazioni, anche in più riprese)
  generateCustomsDocuments: async (shipmentId: number, data: GenerateCustomsDeclarationsPayload) => {
    const response = await apiClient.post(`/unified-shipments/${shipmentId}/generate-customs-documents`, data);
    return response.data;
  },

  // Documento di carico in bozza (DDT, Packing List, Altro) di una spedizione
  // NAZIONALE/UE arrivata: la merce entra in magazzino con l'Elabora del documento
  generateLoadDocument: async (shipmentId: number, data: GenerateLoadDocumentPayload) => {
    const response = await apiClient.post(`/unified-shipments/${shipmentId}/generate-load-document`, data);
    return response.data;
  },

  // Fatture e packing list del fornitore della spedizione (v3.8.0)
  getInvoices: async (shipmentId: number): Promise<SupplierInvoiceListResponse> => {
    const response = await apiClient.get(`/unified-shipments/${shipmentId}/invoices`);
    return response.data;
  },
  createInvoice: async (shipmentId: number, data: SupplierInvoicePayload) => {
    const response = await apiClient.post(`/unified-shipments/${shipmentId}/invoices`, data);
    return response.data;
  },
  updateInvoice: async (shipmentId: number, invoiceId: number, data: SupplierInvoicePayload) => {
    const response = await apiClient.put(`/unified-shipments/${shipmentId}/invoices/${invoiceId}`, data);
    return response.data;
  },
  deleteInvoice: async (shipmentId: number, invoiceId: number) => {
    const response = await apiClient.delete(`/unified-shipments/${shipmentId}/invoices/${invoiceId}`);
    return response.data;
  },
  assignInvoice: async (shipmentId: number, invoiceId: number | null, itemIds: number[]) => {
    const response = await apiClient.put(`/unified-shipments/${shipmentId}/invoice-assignments`, { invoice_id: invoiceId, item_ids: itemIds });
    return response.data;
  },

  // Documenti di transito T1 (solo riferimento, nessuna merce mossa) - v3.10.0
  getTransitDocuments: async (shipmentId: number): Promise<{ shipment_id: number; documents: TransitDocument[] }> => {
    const response = await apiClient.get(`/unified-shipments/${shipmentId}/transit-documents`);
    return response.data;
  },
  createTransitDocument: async (shipmentId: number, data: TransitDocumentPayload) => {
    const response = await apiClient.post(`/unified-shipments/${shipmentId}/transit-documents`, data);
    return response.data;
  },
  updateTransitDocument: async (shipmentId: number, docId: number, data: TransitDocumentPayload) => {
    const response = await apiClient.put(`/unified-shipments/${shipmentId}/transit-documents/${docId}`, data);
    return response.data;
  },
  deleteTransitDocument: async (shipmentId: number, docId: number) => {
    const response = await apiClient.delete(`/unified-shipments/${shipmentId}/transit-documents/${docId}`);
    return response.data;
  },
};

export interface TransitDocumentPayload {
  document_number: string;
  document_date: string;
  origin_location?: string | null;
  destination_location?: string | null;
  notes?: string | null;
}

export interface TransitDocument extends TransitDocumentPayload {
  id: number;
  document_type: 'T1';
  shipment_id: number;
}

export interface SupplierInvoicePayload {
  supplier_id: number;
  invoice_number: string;
  invoice_date?: string | null;
  packing_list_number?: string | null;
  packing_list_date?: string | null;
  currency?: string | null;
  total_amount?: number | null;
  notes?: string | null;
  item_ids?: number[];
}

export interface SupplierInvoice {
  id: number;
  shipment_id: number;
  supplier_id: number;
  supplier_name: string | null;
  invoice_number: string;
  invoice_date: string | null;
  packing_list_number: string | null;
  packing_list_date: string | null;
  currency: string | null;
  total_amount: number | null;
  notes: string | null;
  items_count: number;
  total_quantity: number;
  documents: { id: number; document_type: string; document_number: string; customs_status: string; quantity: number }[];
}

export interface SupplierInvoiceListResponse {
  shipment_id: number;
  invoices: SupplierInvoice[];
  unassigned_items: number;
}

// ============================================================
// User Column Preferences API
// ============================================================
export const userColumnPreferencesAPI = {
  /**
   * Recupera preferenze colonne per pagina specifica
   */
  getPreferences: async (pageCode: string): Promise<UserColumnPreferencesResponse> => {
    const response = await apiClient.get(`/user-column-preferences/${pageCode}`);
    return response.data;
  },

  /**
   * Salva preferenze colonne (bulk upsert)
   */
  savePreferences: async (
    pageCode: string,
    preferences: Record<string, boolean>
  ): Promise<UserColumnPreferencesResponse> => {
    const requestData: UserColumnPreferencesRequest = {
      page_code: pageCode,
      preferences,
    };
    const response = await apiClient.post(`/user-column-preferences/${pageCode}`, requestData);
    return response.data;
  },

  /**
   * Ripristina preferenze ai valori default
   */
  resetPreferences: async (pageCode: string) => {
    const response = await apiClient.delete(`/user-column-preferences/${pageCode}`);
    return response.data;
  },
};

export const reportAPI = {
  exportGiacenzeExcel: async (customer_id?: number, warehouse_id?: number, only_with_stock?: boolean) => {
    const params: any = {};
    if (customer_id) params.customer_id = customer_id;
    if (warehouse_id) params.warehouse_id = warehouse_id;
    if (only_with_stock) params.only_with_stock = true;
    const response = await apiClient.get('/reports/giacenze/excel', {
      params,
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'report_giacenze.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response.data;
  },

  // Report Situazione Cliente: vista JSON per la pagina a video
  getSituazioneCliente: async (customer_id: number, only_with_data?: boolean) => {
    const params: any = { customer_id };
    if (only_with_data) params.only_with_data = true;
    const response = await apiClient.get('/reports/situazione-cliente', { params });
    return response.data;
  },

  // Report Situazione Cliente: export Excel (stesse righe della vista)
  exportSituazioneClienteExcel: async (customer_id: number, only_with_data?: boolean) => {
    const params: any = { customer_id };
    if (only_with_data) params.only_with_data = true;
    const response = await apiClient.get('/reports/situazione-cliente/excel', {
      params,
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'situazione_cliente.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response.data;
  },
};

// Formazione-Tmp API (solo Admin)
export const formazioneAPI = {
  listDocuments: async () => {
    const response = await apiClient.get('/formazione/documents');
    return response.data;
  },

  validate: async (file: File, documentId: number, targa: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_id', String(documentId));
    formData.append('targa', targa);
    const response = await apiClient.post('/formazione/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  execute: async (file: File, documentId: number, targa: string, conflictMode: string = 'abort') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_id', String(documentId));
    formData.append('targa', targa);
    formData.append('conflict_mode', conflictMode);
    const response = await apiClient.post('/formazione/execute', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
};

// Nazionalizza Prodotti (pulsante in Documenti Doganali): sdoganamento con IM4 di merce già in magazzino doganale
export interface NationalizationLinePayload {
  stock_id: number;
  quantity: number;
}

export interface NationalizationPayload {
  source_warehouse_id: number;
  lines: NationalizationLinePayload[];
  destination_warehouse_id?: number;
  document_number?: string;
  document_date?: string;
  mnr_code?: string;
  notes?: string;
}

export const customsNationalizationAPI = {
  getWarehouses: async () => {
    const response = await apiClient.get('/customs-nationalization/warehouses');
    return response.data;
  },
  getCandidates: async (warehouseId: number) => {
    const response = await apiClient.get('/customs-nationalization/candidates', { params: { warehouse_id: warehouseId } });
    return response.data;
  },
  preview: async (payload: NationalizationPayload) => {
    const response = await apiClient.post('/customs-nationalization/preview', payload);
    return response.data;
  },
  execute: async (payload: NationalizationPayload) => {
    const response = await apiClient.post('/customs-nationalization/execute', payload, { timeout: 300000 });
    return response.data;
  },
};

// Formazione automatica da file del magazzino (menu Magazzino → Formazione)
export const formazioneAutoAPI = {
  // shipments: {foglio: id spedizione} scelto per le targhe condivise da più spedizioni
  validate: async (file: File, shipments?: Record<string, number>) => {
    const formData = new FormData();
    formData.append('file', file);
    if (shipments && Object.keys(shipments).length) formData.append('shipments', JSON.stringify(shipments));
    const response = await apiClient.post('/formazione-auto/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  // Applica la formazione e scarica automaticamente il file di esito (verde = formate, rosso = saltate)
  // shipments: {foglio: id spedizione} CONFERMATO dall'utente (targhe condivise da più spedizioni)
  execute: async (file: File, sheets?: string[], assignments?: Record<string, Record<string, Record<string, string[]>>>,
    shipments?: Record<string, number>) => {
    const formData = new FormData();
    formData.append('file', file);
    if (sheets) formData.append('sheets', JSON.stringify(sheets));
    if (assignments) formData.append('assignments', JSON.stringify(assignments));
    if (shipments && Object.keys(shipments).length) formData.append('shipments', JSON.stringify(shipments));
    const response = await apiClient.post('/formazione-auto/execute', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    const data = response.data;
    if (data?.file?.content_base64) {
      await downloadGiacenzeFiles({ files: [data.file] });
    }
    return data;
  },

  // Registro esecuzioni (una per foglio/container) e rollback
  listRuns: async (params?: { limit?: number; shipment_id?: number }) => {
    const response = await apiClient.get('/formazione-auto/runs', { params });
    return response.data;
  },
  getRun: async (runId: number) => {
    const response = await apiClient.get(`/formazione-auto/runs/${runId}`);
    return response.data;
  },
  rollbackRun: async (runId: number, notes?: string) => {
    const response = await apiClient.post(`/formazione-auto/runs/${runId}/rollback`, { notes: notes || undefined });
    return response.data;
  },
};

// Confronto Giacenze Excel (Utilità): valida 3 file e produce la copia colorata di A
// Decodifica i file base64 restituiti dai controlli giacenze e avvia i download
// (uno o più file, con piccolo stagger per affidabilità sui browser).
const downloadGiacenzeFiles = async (data: any) => {
  const stats = data.stats || {};
  const files: Array<{ key: string; filename: string; content_base64: string }> = data.files || [];

  const b64ToBlob = (b64: string) => {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const url = window.URL.createObjectURL(b64ToBlob(f.content_base64));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', f.filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    if (i < files.length - 1) await sleep(500);
  }

  return { stats, filenames: files.map((f) => f.filename) };
};

// Utilità "Controllo Quantità con Oron" (A vs B) — colora la copia di A su Q.TA'
export const controlloQuantitaAPI = {
  validate: async (fileA: File, fileB: File) => {
    const formData = new FormData();
    formData.append('file_a', fileA);
    formData.append('file_b', fileB);
    // Timeout esteso: file fino a 30 MB e conversione LibreOffice di .xls/.ods
    const response = await apiClient.post('/giacenze-compare/quantita/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  process: async (fileA: File, fileB: File) => {
    const formData = new FormData();
    formData.append('file_a', fileA);
    formData.append('file_b', fileB);
    const response = await apiClient.post('/giacenze-compare/quantita/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return downloadGiacenzeFiles(response.data);
  },
};

// Utilità "Controllo UDC" (A vs C) — colora le copie di A (UDC COLLO) e C (Codice)
export const controlloUdcAPI = {
  validate: async (fileA: File, fileC: File) => {
    const formData = new FormData();
    formData.append('file_a', fileA);
    formData.append('file_c', fileC);
    const response = await apiClient.post('/giacenze-compare/udc/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  process: async (fileA: File, fileC: File) => {
    const formData = new FormData();
    formData.append('file_a', fileA);
    formData.append('file_c', fileC);
    const response = await apiClient.post('/giacenze-compare/udc/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return downloadGiacenzeFiles(response.data);
  },
};

// Utilità "Controllo Fattura Corriere" (A spedizioni vs B fattura PDF BRT)
export const controlloFatturaCorriereAPI = {
  validate: async (fileA: File, fileB: File, tolerance: number) => {
    const formData = new FormData();
    formData.append('file_a', fileA);
    formData.append('file_b', fileB);
    formData.append('tolerance', String(tolerance));
    const response = await apiClient.post('/fattura-corriere/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  process: async (fileA: File, fileB: File, tolerance: number) => {
    const formData = new FormData();
    formData.append('file_a', fileA);
    formData.append('file_b', fileB);
    formData.append('tolerance', String(tolerance));
    const response = await apiClient.post('/fattura-corriere/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return downloadGiacenzeFiles(response.data);
  },
};

// Impostazioni provider AI (solo Admin) — le chiavi API sono write-only
export const aiSettingsAPI = {
  getSettings: async () => {
    const response = await apiClient.get('/ai-settings/');
    return response.data;
  },

  updateSettings: async (payload: {
    anthropic_api_key?: string;
    openai_api_key?: string;
    google_api_key?: string;
    active_provider?: string;
    active_model?: string;
    monthly_budget_usd?: number;
    model_prices?: Record<string, { input: number; output: number }>;
  }) => {
    const response = await apiClient.put('/ai-settings/', payload);
    return response.data;
  },

  listModels: async (provider: string) => {
    const response = await apiClient.get(`/ai-settings/models/${provider}`, {
      timeout: 60000,
    });
    return response.data;
  },
};

// Importa da PI: proforma del fornitore → ordine fornitore (risorsa supplier-orders) — v3.4.0
export const supplierOrderAIImportAPI = {
  analyze: async (files: File[], force = false) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('force', force ? 'true' : 'false');
    const response = await apiClient.post('/supplier-order-ai-import/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },
  confirm: async (payload: {
    import_ids: number[];
    mode: 'new' | 'append';
    supplier_id: number;
    existing_order_id?: number;
    order?: { order_number: string; order_date?: string; order_status_id: number; customer_order_id?: number; stock_code?: string; delivery_date?: string; notes?: string };
    lines: { import_id: number; row: number; product_id: number | null; new_product: any | null; skip: boolean; customer_order_id: number | null }[];
  }) => {
    const response = await apiClient.post('/supplier-order-ai-import/confirm', payload, { timeout: 120000 });
    return response.data;
  },
  discard: async (importId: number) => {
    const response = await apiClient.post(`/supplier-order-ai-import/${importId}/discard`);
    return response.data;
  },
};

// Importa spedizione da documenti: fattura + packing list + BL (risorsa unified-shipments) — v3.5.0
export const shipmentAIImportAPI = {
  analyze: async (files: File[], force = false) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));
    formData.append('force', force ? 'true' : 'false');
    const response = await apiClient.post('/shipment-ai-import/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },
  confirm: async (payload: {
    import_ids: number[]; supplier_id: number; force_code?: boolean;
    groups?: { key: string; supplier_id?: number }[];
    header: { shipment_code: string; shipment_type: string; warehouse_id: number; transport_type_id: number; container_type_id?: number; importer_id?: number;
      transport_document_type?: string; transport_document_number?: string; carrier_name?: string; vehicle_plate?: string; departure_date?: string; expected_arrival_date?: string; description?: string; notes?: string };
    lines: { row: number; product_id: number | null; supplier_order_detail_id: number | null; quantity: number | null; skip: boolean }[];
  }) => {
    const response = await apiClient.post('/shipment-ai-import/confirm', payload, { timeout: 120000 });
    return response.data;
  },
  discard: async (importId: number) => {
    const response = await apiClient.post(`/shipment-ai-import/${importId}/discard`);
    return response.data;
  },
};

// Importa con AI in dogana: bollette IM7/IM4 → CustomsSplitModal precompilato (risorsa unified-shipments) — v3.6.0
export const customsAIImportAPI = {
  analyze: async (shipmentId: number, files: File[], force = false) => {
    const formData = new FormData();
    formData.append('shipment_id', String(shipmentId));
    files.forEach(f => formData.append('files', f));
    formData.append('force', force ? 'true' : 'false');
    const response = await apiClient.post('/customs-ai-import/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },
  confirm: async (importIds: number[], customsDocumentIds: number[]) => {
    const response = await apiClient.post('/customs-ai-import/confirm', { import_ids: importIds, customs_document_ids: customsDocumentIds });
    return response.data;
  },
  discard: async (importId: number) => {
    const response = await apiClient.post(`/customs-ai-import/${importId}/discard`);
    return response.data;
  },
};

// Registro delle importazioni da documento con AI (solo admin, Impostazioni → API)
export const documentImportAPI = {
  list: async (params: {
    document_type?: string; status?: string; created_by?: number; search?: string; skip?: number; limit?: number;
  }) => {
    const response = await apiClient.get('/document-imports/', { params });
    return response.data;
  },
  get: async (id: number) => {
    const response = await apiClient.get(`/document-imports/${id}`);
    return response.data;
  },
  budget: async () => {
    const response = await apiClient.get('/document-imports/budget');
    return response.data;
  },
};

// Corrispondenze delle importazioni (solo admin, Impostazioni → Corrispondenze)
export const importAliasAPI = {
  listProducts: async (params: { supplier_id?: number; search?: string; include_inactive?: boolean; skip?: number; limit?: number }) => {
    const response = await apiClient.get('/import-aliases/products', { params });
    return response.data;
  },
  updateProduct: async (id: number, payload: { product_id?: number; is_active?: boolean }) => {
    const response = await apiClient.put(`/import-aliases/products/${id}`, payload);
    return response.data;
  },
  deleteProduct: async (id: number) => {
    const response = await apiClient.delete(`/import-aliases/products/${id}`);
    return response.data;
  },
  listParties: async (params: { party_type?: string; search?: string; include_inactive?: boolean; skip?: number; limit?: number }) => {
    const response = await apiClient.get('/import-aliases/parties', { params });
    return response.data;
  },
  updateParty: async (id: number, payload: { party_id?: number; is_active?: boolean }) => {
    const response = await apiClient.put(`/import-aliases/parties/${id}`, payload);
    return response.data;
  },
  deleteParty: async (id: number) => {
    const response = await apiClient.delete(`/import-aliases/parties/${id}`);
    return response.data;
  },
  listMarks: async (params: { supplier_id?: number; search?: string; include_inactive?: boolean; skip?: number; limit?: number }) => {
    const response = await apiClient.get('/import-aliases/marks', { params });
    return response.data;
  },
  updateMark: async (id: number, payload: { customer_order_id?: number; customer_id?: number; is_active?: boolean; clear_order?: boolean }) => {
    const response = await apiClient.put(`/import-aliases/marks/${id}`, payload);
    return response.data;
  },
  deleteMark: async (id: number) => {
    const response = await apiClient.delete(`/import-aliases/marks/${id}`);
    return response.data;
  },
  listDestinations: async (params: { customer_id?: number; search?: string; include_inactive?: boolean; skip?: number; limit?: number }) => {
    const response = await apiClient.get('/import-aliases/destinations', { params });
    return response.data;
  },
  updateDestination: async (id: number, payload: { shipping_destination_id?: number; is_active?: boolean }) => {
    const response = await apiClient.put(`/import-aliases/destinations/${id}`, payload);
    return response.data;
  },
  deleteDestination: async (id: number) => {
    const response = await apiClient.delete(`/import-aliases/destinations/${id}`);
    return response.data;
  },
  testMatch: async (params: { kind: string; text: string; supplier_id?: number; size?: string }) => {
    const response = await apiClient.get('/import-aliases/test-match', { params });
    return response.data;
  },
};

// Carica Ordine Cliente con AI (tutti gli utenti autenticati) — motore document_import (v3.3.0)
export const aiOrderImportAPI = {
  // L'analisi LLM può durare minuti: timeout per-request esteso. force = rianalizza un file già importato
  analyze: async (file: File, force = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('force', force ? 'true' : 'false');
    const response = await apiClient.post('/customer-order-ai-import/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  confirm: async (payload: {
    import_id: number;
    customer_id: number;
    shipping_destination_id?: number;
    order_number?: string;
    lines: { row: number; product_id: number | null; skip: boolean }[];
    force_duplicate?: boolean;
  }) => {
    const response = await apiClient.post('/customer-order-ai-import/confirm', payload, { timeout: 60000 });
    return response.data;
  },

  discard: async (importId: number) => {
    const response = await apiClient.post(`/customer-order-ai-import/${importId}/discard`);
    return response.data;
  },
};

// Esporta anche il client API per uso diretto quando necessario
export { default as apiClient } from '../utils/axiosConfig.ts';
